import React from 'react';
import memoizeOne from 'memoize-one';
import { graphql, useFragment } from 'react-relay';

import GoalsInlineEditViewField from '@atlassian/jira-business-goals-issue-view-field';
import { ff } from '@atlassian/jira-feature-flagging';
import { IssueViewCascadingSelectField } from '@atlassian/jira-issue-view-layout-cascading-select-field';
import {
	IssueViewLabelsSystemField,
	type IssueViewLabelsSystemFieldFragment,
} from '@atlassian/jira-issue-view-layout-labels-system-field';
import { IssueViewSingleSelectField } from '@atlassian/jira-issue-view-layout-single-select-field';
import { SentimentField } from '@atlassian/jira-issue-view-sentiment';
import type { issueViewEntitlementField_servicedeskCustomerServiceEntitlementsField_EntitlementsField$key } from '@atlassian/jira-relay/src/__generated__/issueViewEntitlementField_servicedeskCustomerServiceEntitlementsField_EntitlementsField.graphql';
import type { relayLayoutItems_issueViewLayoutTemplatesLayoutItem_RelayLayoutItemFragmentContainer$key as RelayLayoutItemFragmentContainerFragment } from '@atlassian/jira-relay/src/__generated__/relayLayoutItems_issueViewLayoutTemplatesLayoutItem_RelayLayoutItemFragmentContainer.graphql';
import type { ui_businessGoalsIssueViewField_GoalsInlineEditViewField$key as GoalsFragment } from '@atlassian/jira-relay/src/__generated__/ui_businessGoalsIssueViewField_GoalsInlineEditViewField.graphql';
import type { ui_issueViewLayoutCascadingSelectField_IssueViewCascadingSelectField$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewLayoutCascadingSelectField_IssueViewCascadingSelectField.graphql';
import type { ui_issueViewLayoutSingleSelectField_IssueViewSingleSelectField$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewLayoutSingleSelectField_IssueViewSingleSelectField.graphql';
import type { ui_issueViewSentiment_SentimentField$key as SentimentFragment } from '@atlassian/jira-relay/src/__generated__/ui_issueViewSentiment_SentimentField.graphql';
import { AsyncEntitlementsField } from '@atlassian/jira-servicedesk-customer-service-entitlements-field';

import type { LayoutItemElementMapGeneric, LayoutItemPropsMapEntry } from './types';
/**
 * Layout items in the map are Relay Components primarily keyed by the relevant __typename.
 * But when the same graphql type is shared by multiple Jira field types, the map may contain a nested mapping object to map the Jira field type to type to the relevant component.
 *
 * To add a new Item to this Map:
 * 1. Add a new entry to this type.
 *  a. How to FF:
 *    i. Make your entry nullable at the relevant level.
 *    ii. eg: {@code GraphqlType: null | LayoutItemMapEntryProps<...>}
 *    iii. Then when cleaning up the flag make the item mandatory.
 *    iv: eg: {@code GraphqlType: LayoutItemMapEntryProps<...>}
 *  b: What types to use for new entries to the type:
 *    i. Use {@link LayoutItemPropsMapEntry} if you only have a single component to register
 *    ii. Use {@link LayoutItemPropsMapEntryWithGlancePanel} if your component triggers a glance panel opening on the RHS of the issue view
 *    iii. Use {@link LayoutItemPropsSplitComponentsMapEntry} is you need to distinguish different components for the same graphql type
 * 2. Add the entry to {@link getLayoutItemMap}
 *  a. How to FF:
 *    i. Use a ternary to switch your new entry to null when the FF is enabled
 *    ii. eg: {@code GraphqlType: isFfEnabled() ? Component : null, }
 * 3. Add the relay fragment of the relevant component to the fragment in {@link RelayLayoutItemSwitcher}
 *  a. Use a ff using the {@code @include(if: $isFfEnabled)} directive with a new graphql query variable
 */
export type LayoutItemPropsMap = {
	JiraCascadingSelectField: null | LayoutItemPropsMapEntry<ui_issueViewLayoutCascadingSelectField_IssueViewCascadingSelectField$key>;
	JiraLabelsField: null | LayoutItemPropsMapEntry<IssueViewLabelsSystemFieldFragment>;
	JiraSingleSelectField: null | LayoutItemPropsMapEntry<ui_issueViewLayoutSingleSelectField_IssueViewSingleSelectField$key>;
	JiraServiceManagementEntitlementField: null | LayoutItemPropsMapEntry<issueViewEntitlementField_servicedeskCustomerServiceEntitlementsField_EntitlementsField$key>;
	JiraGoalsField: null | LayoutItemPropsMapEntry<GoalsFragment>;
	JiraServiceManagementSentimentField: null | LayoutItemPropsMapEntry<SentimentFragment>;
};
/**
 * Calculated literal type of the expected return value of {@link getLayoutItemMap}. See {@link LayoutItemElementMapGeneric} for how this calculation is performed.
 */
export type LayoutItemElementMap = LayoutItemElementMapGeneric<LayoutItemPropsMap>;

/**
 * See {@link LayoutItemPropsMap} for instructions.
 * Memoized function instead of static object because we want it to be initialised once in a react 'render' lifecycle for FFing changes and easier mocking
 * You may need to cast the output to  {@link LayoutItemElementMapNonLiteral} if you need to access the map with a non-literal string
 */
export const getLayoutItemMap = memoizeOne(
	(): LayoutItemElementMap => ({
		JiraCascadingSelectField: ff(
			'issue.details.relay-cascading-select-field-issue-view-layout-integration',
		)
			? {
					item: IssueViewCascadingSelectField,
				}
			: null,
		JiraLabelsField: {
			item: IssueViewLabelsSystemField,
		},
		JiraSingleSelectField: {
			item: IssueViewSingleSelectField,
		},
		JiraServiceManagementEntitlementField: {
			item: AsyncEntitlementsField,
		},
		JiraGoalsField: ff('hrz-716-integrate-goal-picker-into-issue-view')
			? {
					item: GoalsInlineEditViewField,
				}
			: null,
		JiraServiceManagementSentimentField: ff('sentiment-custom-field')
			? {
					item: SentimentField,
				}
			: null,
	}),
);

type RelayLayoutItemSwitcherProps = {
	fragmentKey: RelayLayoutItemFragmentContainerFragment | null;
	area: string;
	// There will be other properties in the payload for layout items registered with {@link LayoutItemPropsWithPanel}. The payload can be specified as the second generic argument
	onOpenPanel: (payload: { title: string }) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Component: any;
	isLast?: boolean;
};

/**
 * Component used to aggregate all layout-controlled components' relay fragments.
 *
 * see {@link LayoutItemPropsMap} for instructions
 */
export const RelayLayoutItemFragmentContainer = ({
	fragmentKey,
	area,
	Component,
	onOpenPanel,
	isLast,
}: RelayLayoutItemSwitcherProps) => {
	const data = useFragment<RelayLayoutItemFragmentContainerFragment>(
		graphql`
			# Fragment on Node so that we can support a union type of non-JiraIssueFields eventually
			fragment relayLayoutItems_issueViewLayoutTemplatesLayoutItem_RelayLayoutItemFragmentContainer on Node
			@argumentDefinitions(
				issueViewRelayCascadingSelectFlag: { type: "Boolean!", defaultValue: false }
				issueViewRelayGoalsFlag: { type: "Boolean!", defaultValue: false }
				jsmSentimentCustomFieldFlag: { type: "Boolean!", defaultValue: false }
			) {
				# Add new fragments for Relay powered fields here:
				...ui_issueViewLayoutLabelsSystemField_IssueViewLabelsSystemField
				...ui_issueViewLayoutSingleSelectField_IssueViewSingleSelectField
				...ui_issueViewSentiment_SentimentField @include(if: $jsmSentimentCustomFieldFlag)
				...ui_issueViewLayoutCascadingSelectField_IssueViewCascadingSelectField
					@include(if: $issueViewRelayCascadingSelectFlag)
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...issueViewEntitlementField_servicedeskCustomerServiceEntitlementsField_EntitlementsField
				...ui_businessGoalsIssueViewField_GoalsInlineEditViewField
					@include(if: $issueViewRelayGoalsFlag)
			}
		`,
		fragmentKey,
	);

	return <Component area={area} fragmentKey={data} onOpenPanel={onOpenPanel} isLast={isLast} />;
};
